<template>
  <div class="timesheet-map-tab timesheet-tab-shell">
    <div class="map-toolbar timesheet-tab-toolbar">
      <div class="timesheet-toolbar-top">
        <div>
          <h2 class="timesheet-tab-title">Map</h2>
          <p class="timesheet-tab-subtitle">
            Manage job site zones and see where tradies are clocked in
          </p>
        </div>
        <div class="timesheet-toolbar-actions">
          <span class="map-stat-pill">
            {{ zones.length }} {{ zones.length === 1 ? 'zone' : 'zones' }}
          </span>
          <span v-if="canCreateZone" class="map-stat-pill map-stat-pill-blue">
            Drawing tools enabled
          </span>
        </div>
      </div>
    </div>

    <div class="map-content timesheet-tab-content">
      <div class="map-container">
        <div class="map-settings">
          <div class="zones-sidebar">
            <div class="sidebar-header">
              <h3>Job site zones</h3>
              <p>Draw zones, track clock-ins</p>
            </div>
            <div v-if="canCreateZone" class="tool-row">
              <button
                v-for="tool in tools"
                :key="tool.id"
                class="tool-btn"
                :class="{ active: activeTool === tool.id }"
                @click="selectTool(tool.id)"
              >
                <i :class="`ti ti-${tool.icon}`" />
                {{ tool.label }}
              </button>
            </div>
            <div class="zones-list">
              <div
                v-for="zone in zones"
                :key="zone.id"
                class="zone-card"
                :class="{ active: selectedZone === zone.id }"
                @click="selectZone(zone.id)"
              >
                <div class="zone-card-header">
                  <span class="zone-name">{{ zone.name }}</span>
                  <span class="zone-badge" :class="`badge-${zone.status}`">
                    {{ zone.status }}
                  </span>
                </div>
                <!-- <div class="zone-meta">{{ zone.address }} {{ zone.coords }}</div> -->
                <div
                  v-if="zone.tradies && zone.tradies.length"
                  class="zone-tradies"
                >
                  <div
                    v-for="t in zone.tradies"
                    :key="t.initials"
                    class="tradie-dot"
                    :class="t.onSite ? 'dot-in' : 'dot-out'"
                  >
                    {{ t.initials }}
                  </div>
                  <span class="tradie-count">
                    {{ zone.tradies.filter((t) => t.onSite).length }} /
                    {{ zone.tradies.length }} on site</span
                  >
                </div>
                <div v-else class="zone-meta" style="margin-top: 6px">
                  No tradies assigned
                </div>
              </div>
            </div>
            <div class="sidebar-footer">
              <button
                type="button"
                class="help-trigger"
                @click="showHelp = true"
              >
                <i class="ti ti-help-circle" />
                How this works
              </button>
            </div>

            <transition name="slide-up">
              <div v-if="showHelp" class="help-panel">
                <div class="help-panel-header">
                  <h4 class="help-panel-title">How this works</h4>
                  <button
                    type="button"
                    class="zone-close-btn"
                    @click="showHelp = false"
                  >
                    <i class="ti ti-x" />
                  </button>
                </div>
                <p>
                  Draw a shape on the map to mark out a job site. Assign a
                  project to that site and its tradies are automatically added,
                  creating a zone they can clock in and out of.
                </p>
                <p>
                  Once a site has a project, it's ready to go - clock-ins will
                  show up against it in the Timesheets tab.
                </p>
                <b-button
                  variant="primary"
                  size="sm"
                  block
                  class="help-panel-close"
                  @click="showHelp = false"
                  >Got it</b-button
                >
              </div>
            </transition>
          </div>
          <div class="map-column">
            <div ref="mapEl" class="map-area"></div>
            <b-collapse :visible="barOpen" @hidden="onBarHidden">
              <div v-if="zoneDraft" class="zone-bottom-bar">
                <div class="zone-bottom-bar-header">
                  <div>
                    <h4 class="zone-bottom-bar-title">
                      {{ selectedZone ? 'Edit zone' : 'New zone' }}
                    </h4>
                    <p class="zone-bottom-bar-subtitle">
                      Assign a project or update zone details
                    </p>
                  </div>
                  <button
                    type="button"
                    class="zone-close-btn"
                    @click="selectZone(null)"
                  >
                    <i class="ti ti-x" />
                  </button>
                </div>

                <b-row class="zone-form-row">
                  <b-col cols="12" md="4">
                    <b-form-group label="Name" label-class="zone-form-label">
                      <b-form-input
                        v-model="zoneDraft.name"
                        placeholder="Zone name"
                        :disabled="!canEditZoneForm"
                      />
                    </b-form-group>
                  </b-col>
                  <!-- <b-col cols="12" md="4">
                  <b-form-group label="Address" label-class="zone-form-label">
                    <b-form-input
                      v-model="zoneDraft.address"
                      placeholder="Job site address"
                    />
                  </b-form-group>
                </b-col> -->
                  <b-col cols="12" md="4">
                    <b-form-group label="Status" label-class="zone-form-label">
                      <b-form-select
                        v-model="zoneDraft.status"
                        :disabled="!canEditZoneForm"
                        :options="[
                          { value: 'active', text: 'Active' },
                          { value: 'inactive', text: 'Inactive' },
                        ]"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group label="Project" label-class="zone-form-label">
                      <b-form-select
                        v-model="zoneDraft.projectId"
                        :disabled="!canEditZoneForm"
                        :options="projectOptions"
                        @change="onProjectSelected"
                      >
                        <template #first>
                          <b-form-select-option :value="null"
                            >Select project</b-form-select-option
                          >
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="zone-bottom-bar-actions">
                  <b-button
                    v-if="selectedZone ? canManageZones : canCreateZone"
                    variant="primary"
                    size="sm"
                    @click="saveZone"
                    >Save</b-button
                  >
                  <b-button
                    v-if="selectedZone && canManageZones"
                    variant="outline-danger"
                    size="sm"
                    @click="onDeleteClick"
                    >Delete</b-button
                  >
                  <b-button variant="light" size="sm" @click="selectZone(null)"
                    >Close</b-button
                  >
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global L */
import { mapGetters, mapActions } from 'vuex'
import { toGeoJsonFeature } from '@/utils/geofence'

export default {
  data() {
    return {
      map: null,
      activeTool: null,
      selectedZone: null,
      drawnItems: null,
      drawHandler: null,

      zoneDraft: null,
      barOpen: false,
      showHelp: false,
      mapResizeObserver: null,

      tools: [
        { id: 'rect', icon: 'square', label: 'Rectangle' },
        // { id: 'poly', icon: 'vector-triangle', label: 'Polygon' },
        { id: 'circle', icon: 'circle', label: 'Radius' },
      ],

      renderedZoneLayers: {},
    }
  },
  watch: {
    zones: {
      immediate: true,
      handler() {
        this.renderLoadedZones()
      },
    },
  },
  computed: {
    ...mapGetters('timesheet', ['zones', 'zone']),
    ...mapGetters('project', ['projects']),
    ...mapGetters('auth', [
      'hasPermission',
      'isProjectScoped',
      'supervisorProjectIds',
    ]),

    canCreateZone() {
      return this.hasPermission('timesheets.zones.create')
    },
    canManageZones() {
      return this.hasPermission('timesheets.zones.manage')
    },

    /**
     * Whether the zone form fields are editable: managing an existing zone needs
     * timesheets.zones.manage, a new zone needs timesheets.zones.create. Mirrors
     * the Save button's gate so view-only users can't change values at all.
     */
    canEditZoneForm() {
      return this.selectedZone ? this.canManageZones : this.canCreateZone
    },

    selectedZoneData() {
      return this.zones.find((zone) => zone.id === this.selectedZone) || null
    },
    projectOptions() {
      return (this.projects || []).map((p) => ({
        value: p.id,
        text: p.name,
      }))
    },
  },
  mounted() {
    const zoneParams =
      this.isProjectScoped && this.supervisorProjectIds.length > 0
        ? { project_ids: this.supervisorProjectIds }
        : {}
    this.getZones(zoneParams)
    this.getProjects({ limit: 100 })
    this.$emit('loading', false)
    this.$nextTick(() => {
      this.initMap()
    })
  },

  beforeDestroy() {
    this.teardownMapResizeObserver()

    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },

  methods: {
    ...mapActions('timesheet', [
      'getZones',
      'getZone',
      'createZone',
      'updateZone',
      'deleteZone',
    ]),
    ...mapActions('project', ['getProjects']),
    renderLoadedZones() {
      if (!this.map || !this.drawnItems) return

      const currentIds = new Set(this.zones.map((z) => String(z.id)))

      Object.keys(this.renderedZoneLayers).forEach((id) => {
        if (!currentIds.has(id)) {
          this.drawnItems.removeLayer(this.renderedZoneLayers[id])
          delete this.renderedZoneLayers[id]
        }
      })

      this.zones.forEach((zone) => {
        if (this.renderedZoneLayers[zone.id]) return
        if (!zone.geojson) return

        // Older zones are stored as bare geometry rather than a Feature, and
        // some have the polygon ring nested a level too shallow. Normalize
        // first so L.geoJSON gets the shape it expects.
        const feature = toGeoJsonFeature(zone.geojson)

        if (!feature) {
          // eslint-disable-next-line no-console
          console.warn(
            `Zone ${zone.id} ("${zone.name}") has unusable geometry and was not drawn.`,
          )
          return
        }

        // One malformed zone used to throw out of this loop and leave every
        // later zone undrawn, so the map looked empty rather than partial.
        try {
          const layer = L.geoJSON(feature, {
            style: { color: '#3b82f6', fillOpacity: 0.2, weight: 2 },
            pointToLayer: (_feature, latlng) => {
              if (zone.radius) {
                return L.circle(latlng, {
                  radius: zone.radius,
                  color: '#3b82f6',
                  fillOpacity: 0.2,
                  weight: 2,
                })
              }
              return L.marker(latlng)
            },
          })
          layer.eachLayer((l) => {
            l.on('click', (event) => {
              L.DomEvent.stopPropagation(event)
              this.selectZone(zone.id)
            })
          })
          this.drawnItems.addLayer(layer)
          this.renderedZoneLayers[zone.id] = layer
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`Zone ${zone.id} ("${zone.name}") failed to draw.`, err)
        }
      })
    },

    initMap() {
      if (this.map || !this.$refs.mapEl) return

      this.map = L.map(this.$refs.mapEl).setView([-31.95, 115.86], 13)
      this.map.attributionControl.setPrefix('')
      this.drawnItems = new L.FeatureGroup()
      this.map.addLayer(this.drawnItems)

      this.renderLoadedZones()

      this.map.on(L.Draw.Event.CREATED, (e) => {
        this.onZoneCreated(e)
      })
      this.map.on('click', () => {
        if (this.activeTool) return

        if (this.zoneDraft && !this.selectedZone) return
        this.selectZone(null)
      })

      L.tileLayer(
        `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${process.env.VUE_APP_MAPTILER_KEY}`,
        {
          maxZoom: 19,
          tileSize: 512,
          zoomOffset: -1,
          attribution:
            '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        },
      ).addTo(this.map)

      this.setupMapResizeObserver()

      this.$nextTick(() => {
        this.map.invalidateSize()
      })
    },

    setupMapResizeObserver() {
      if (!this.$refs.mapEl || typeof ResizeObserver === 'undefined') return

      let resizeFrame = null

      this.mapResizeObserver = new ResizeObserver(() => {
        if (resizeFrame) return

        resizeFrame = requestAnimationFrame(() => {
          resizeFrame = null
          if (this.map) this.map.invalidateSize()
        })
      })

      this.mapResizeObserver.observe(this.$refs.mapEl)
    },

    teardownMapResizeObserver() {
      if (!this.mapResizeObserver) return

      this.mapResizeObserver.disconnect()
      this.mapResizeObserver = null
    },
    selectTool(toolId) {
      if (toolId === this.activeTool) {
        this.activeTool = null
        this.disableDraw()
        return
      }
      this.activeTool = toolId
      this.disableDraw()

      const shapeOptions = {
        shapeOptions: {
          color: '#3b82f6',
          fillOpacity: 0.2,
        },
      }

      if (toolId === 'rect') {
        this.drawHandler = new L.Draw.Rectangle(this.map, shapeOptions)
      }
      // else if (toolId === 'poly') {
      //   this.drawHandler = new L.Draw.Polygon(this.map, shapeOptions)
      // }
      else if (toolId === 'circle') {
        this.drawHandler = new L.Draw.Circle(this.map, shapeOptions)
      }

      if (this.drawHandler) {
        this.drawHandler.enable()
      }
    },

    disableDraw() {
      if (this.drawHandler) {
        this.drawHandler.disable()
        this.drawHandler = null
      }
    },

    onBarHidden() {
      this.$nextTick(() => {
        if (!this.barOpen) this.zoneDraft = null
      })
    },

    selectZone(id) {
      // if this is a zone draft and layer and isnt selectedZone, then we remove zonedrafter layer from drawn items
      if (this.zoneDraft && this.zoneDraft.layer && !this.selectedZone) {
        this.drawnItems.removeLayer(this.zoneDraft.layer)
      }
      this.selectedZone = id

      if (id) {
        this.zoneDraft = { ...this.selectedZoneData }
        this.barOpen = true
      } else {
        this.barOpen = false
      }

      this.updateZoneStyles()
    },

    async saveZone() {
      const payload = {
        zone_name: this.zoneDraft.name,
        project_id: this.zoneDraft.projectId,
        geo_json: this.zoneDraft.geojson,
        radius: this.zoneDraft.radius,
        is_active: this.zoneDraft.status === 'active',
      }

      const existsInStore = this.zones.some((z) => z.id === this.selectedZone)

      try {
        if (existsInStore) {
          await this.updateZone({ zone_id: this.selectedZone, ...payload })
        } else {
          await this.createZone(payload)
          if (this.zoneDraft && this.zoneDraft.layer && this.drawnItems) {
            this.drawnItems.removeLayer(this.zoneDraft.layer)
          }
        }
        this.selectZone(null)
      } catch (err) {
        console.error('Save failed', err)
        this.$bvToast.toast('Failed to save zone. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async onDeleteClick() {
      if (!this.selectedZone) return
      const id = this.selectedZone
      try {
        await this.deleteZone(id)
        if (this.renderedZoneLayers[id] && this.drawnItems) {
          this.drawnItems.removeLayer(this.renderedZoneLayers[id])
          delete this.renderedZoneLayers[id]
        }
        this.selectZone(null)
      } catch (err) {
        console.error('Delete failed', err)
        this.$bvToast.toast('Failed to delete zone. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    updateZoneStyles() {
      this.zones.forEach((zone) => {
        const layer = this.renderedZoneLayers[zone.id]
        if (!layer) return

        const isSelected = zone.id === this.selectedZone
        layer.setStyle({
          color: isSelected ? '#1d4ed8' : '#3b82f6',
          fillOpacity: isSelected ? 0.5 : 0.15,
          weight: isSelected ? 4 : 2,
        })
      })
    },

    onZoneCreated(e) {
      const layer = e.layer

      if (this.zoneDraft && this.zoneDraft.layer && !this.selectedZone) {
        this.drawnItems.removeLayer(this.zoneDraft.layer)
      }

      this.drawnItems.addLayer(layer)

      const geojson = layer.toGeoJSON()
      // get radius
      let radius = null
      if (e.layerType === 'circle') {
        radius = layer.getRadius()
      }
      this.zoneDraft = {
        name: `Zone ${this.zones.length + 1}`,
        status: 'active',
        // address: '',
        projectId: null,
        geojson,
        radius,
        layer,
      }
      this.selectedZone = null
      this.barOpen = true

      this.disableDraw()
      this.activeTool = null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.map-stat-pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
}

.map-stat-pill-blue {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}

.map-content {
  display: flex;
  flex-direction: column;
}

.map-container {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  min-height: 720px;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.map-settings {
  display: flex;
  height: 720px;
}

.zones-sidebar {
  position: relative;
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
}

.sidebar-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  p {
    font-size: 12px;
    color: #6b7280;
    margin: 2px 0 0;
  }
}

.tool-row {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.tool-btn {
  flex: 1;
  padding: 7px 4px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  i {
    font-size: 18px;
  }

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #1d4ed8;
  }
}

.sidebar-footer {
  margin-top: auto;
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.help-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  i {
    font-size: 16px;
  }

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
}

.help-panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: 18px 18px 20px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.12);

  p {
    margin: 0 0 10px;
    font-size: 14px;
    line-height: 1.55;
    color: #4b5563;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.help-panel-close {
  margin-top: 14px;
}

.help-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.help-panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.zones-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.zone-card {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    border-color: #3b82f6;
    background: #eff6ff;
  }
}

.zone-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.zone-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.zone-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;

  &.badge-active {
    background: #dcfce7;
    color: #166534;
  }

  &.badge-inactive {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.zone-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.zone-tradies {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
}

.tradie-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  border: 2px solid #fff;

  &.dot-in {
    background: #bbf7d0;
    color: #14532d;
  }

  &.dot-out {
    background: #e5e7eb;
    color: #6b7280;
  }
}

.tradie-count {
  font-size: 11px;
  color: #9ca3af;
  margin-left: 4px;
}

.map-area {
  flex: 1;
  min-height: 0px;
  z-index: 0;
}

.map-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.zone-bottom-bar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.zone-bottom-bar {
  flex-shrink: 0;
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06);
}

.zone-bottom-bar-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.zone-bottom-bar-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.zone-close-btn {
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 4px;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #111827;
  }
}

.zone-form-row {
  margin-top: 12px;
}

.zone-form-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.zone-bottom-bar-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
}

// Optional: tighten bootstrap-vue inputs inside the bar
.zone-bottom-bar {
  /deep/ .form-control,
  /deep/ .custom-select {
    font-size: 13px;
    border-color: #d1d5db;
    border-radius: 6px;

    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
  }
}

/* Small screens: the fixed 280px sidebar would swallow the whole viewport and
 * leave only a sliver of map peeking out on the right. Stack instead — compact
 * zones panel on top (its list scrolling internally), map below at a usable
 * height. Kept last in this block so it wins the cascade over the base rules. */
@media (max-width: 767.98px) {
  .map-container {
    min-height: 0;
  }

  .map-settings {
    flex-direction: column;
    height: auto;
  }

  .zones-sidebar {
    width: 100%;
    max-height: 320px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .map-column {
    flex: none;
  }

  .map-area {
    flex: none;
    height: 420px;
  }
}
</style>
